import { useEffect, useRef, useState } from 'react';
import { ScheduleQueries } from '@frontend/api-schedule';
import { useTranslation } from '@frontend/i18n';
import { useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { useAppScopeStore } from '@frontend/scope';
import { WivPromotionCard } from '@frontend/self-upgrade';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Heading, useModalControl } from '@frontend/design-system';
import { History } from './components/history';
import { Insurance } from './components/insurance';
import { InsuranceBenefitSummary } from './components/insurance-benefit-summary';
import { InsuranceLoader } from './components/insurance-loader';
import { InsuranceTypeChips } from './components/insurance-type-chips';
import NoInsuranceAccess from './components/no-insurance-access';
import { useOpenInsuranceReport } from './hooks';
import { useShouldShowInsurance } from './hooks/use-should-show-insurance';
import { useInsuranceContext } from './providers/insurance-provider';
import { DateRangeOptionValue } from './utils/constants';
import { getNoInsuranceAccessMessage } from './utils/utils';

export const InsuranceContainer = () => {
  const ref = useRef<HTMLElement>(null);
  const { t } = useTranslation();
  const verificationMetricModalControl = useModalControl();
  const {
    locationId,
    isLoading: isInsuranceDataLoading,
    shouldShowTuuthFairyReport,
    requestId,
    isInsuranceStatusInProgress,
    isEnhancedInsuranceVerificationEnabled,
  } = useInsuranceContext();
  const {
    isLoading: isWeaveTeaserLoading,
    showNoInsuranceAccessMessage,
    showWeaveVerifyPromotion,
  } = useShouldShowInsurance(locationId);
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();
  const { getHasInsuranceVerificationAccessForLocation } = useHasWIVOrEIVAccess();
  const { isLoading: isLoadingInsuranceReport } = useOpenInsuranceReport({
    requestId,
    shouldShowTuuthFairyReport,
  });
  const { scrollTo, setScrollTo } = useContactPanelShallowStore('scrollTo', 'setScrollTo');

  const [defaultDateRange, setDefaultDateRange] = useState<DateRangeOptionValue>('today');

  const showInsurance = getHasInsuranceVerificationAccessForLocation(locationId);

  const { data: locationData, isLoading: isListLocationsLoading } = ScheduleQueries.useListLocations(locationId);
  const isIntegratedOffice = locationData?.isIntegrated || false;

  useEffect(() => {
    if (scrollTo === 'insurance-details') {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollTo(undefined);
    }
  }, [scrollTo, setScrollTo]);

  const isLoading =
    isInsuranceDataLoading || isWeaveTeaserLoading || isLoadingInsuranceReport || isListLocationsLoading;

  const updateDefaultDateRange = (range: DateRangeOptionValue) => {
    setDefaultDateRange(range);
    verificationMetricModalControl.openModal();
  };

  if (!showInsurance) return null;

  if (showNoInsuranceAccessMessage) {
    /**
          This message is shown only if it's a multi-location setup where child locations
          do have insurance access, but the current specific location does not.
       */
    const noAccessMessage = getNoInsuranceAccessMessage({
      accessibleLocationsData: accessibleLocationData,
      currentLocationId: locationId,
      selectedLocationIds,
      t,
    });

    return <NoInsuranceAccess message={noAccessMessage} />;
  } else if (showWeaveVerifyPromotion) {
    return (
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1), padding: theme.spacing(2) }}>
        <Heading level={3}>{t('Insurance')}</Heading>
        <WivPromotionCard locationIds={[locationId]} source='contacts' />
      </div>
    );
  }
  return (
    <>
      <InsuranceTypeChips isEnhancedInsuranceVerificationEnabled={isEnhancedInsuranceVerificationEnabled} />
      {isLoading ? (
        <InsuranceLoader />
      ) : (
        <>
          <Insurance
            ref={ref}
            defaultDateRange={defaultDateRange}
            updateDefaultDateRange={updateDefaultDateRange}
            verificationMetricModalControl={verificationMetricModalControl}
            isIntegratedOffice={isIntegratedOffice}
          />

          {!isInsuranceStatusInProgress && <InsuranceBenefitSummary />}
          {!isInsuranceStatusInProgress && <History />}
        </>
      )}
    </>
  );
};
InsuranceContainer.displayName = 'InsuranceContainer';
